import { ConfirmAlert } from "../components/Alerts/Alerts";
import ConfirmUsage from "../components/Billing/ConfirmUsage";
import {
  ACTIONS_FREE_BY_TYPE_OF_PLAN,
  ADS_ACTION_TYPES,
  FACEBOOK,
  GOOGLE,
  LINKEDIN,
  MAX_BYTES_STORAGE,
  META,
  PLAN_TYPE_CREDITS,
  PLAN_TYPE_PACKAGES,
  RECURRENT_MULTIPLIER,
  TIKTOK,
  TIKTOK_BUSINESS,
} from "../utils/constants";
import {
  getRoleCapabilities as resolveRoleCapabilities,
  hasRoleCapability,
} from "../utils/roleCapabilities";
import {
  setModalOpenState,
  setShowModalSelectSubscription,
  showBrandUsageLimitAlert,
} from "./ui";
import {
  BUTTON_CONTINUE,
  BUY_CREDITS,
  CONFIRMATION_USAGE_TEXT,
  MODAL_PURCHASE_CREDITS_UPGRADE_PLAN,
  VIDEO_INSUFFICIENT_CREDITS_ALERT_INTRO,
  VIDEO_INSUFFICIENT_CREDITS_ALERT_TITLE,
} from "../i18n/keysTranslations";
import i18next from "i18next";
import {
  usesVideoMeterOnCreditsPlan,
  resolveActionCostForPlanType,
  resolveCampaignUsageMultiplier,
  resolveBrandUsageGate,
} from "../utils/creditActionCosts";
import { startGetIsAppSumo } from "./payments";
import { startGetByokConfiguration, startSaveByokConfiguration } from "./byok";

const resolveBrandUsageGateFromState = ({
  getState,
  usageKey,
  agencyRemaining,
  requiredSpend = 0,
}) => {
  const payments = getState()?.payments || {};
  return resolveBrandUsageGate({
    agencyRemaining,
    allocation: payments.brandUsageAllocation,
    consumed: payments.brandUsageConsumed,
    usageKey,
    creditPricingVersion: payments.creditPricingVersion,
    requiredSpend,
  });
};

export const getNameBusinessTheme = () => (_, getState) => {
  //eslint-disable-next-line
  return getState()?.ui?.theme?.nameBusiness;
};

export const getUserID = () => (_, getState) => {
  return getState()?.auth?.user?.uid;
};
export const getUserEmail = () => (_, getState) => {
  return getState()?.auth?.user?.email;
};

export const getAdAccountUrlMeta = (adAccountID) => () => {
  return `https://business.facebook.com/adsmanager/manage/accounts?act=${adAccountID}`;
};
export const getCustomAudiencesMeta = () => (_, getState) => {
  const integrations = getState()?.user?.integrations;
  const metaAccountBusinessID = integrations?.facebook?.idBusiness;

  return `https://adsmanager.facebook.com/adsmanager/audiences?act=${
    metaAccountBusinessID ? metaAccountBusinessID.split("act_")[1] : ""
  }`;
};

export const getCreateCustomAudienceLink = (platform, adAccountID) => () => {
  if (platform === META) {
    return `https://adsmanager.facebook.com/adsmanager/audiences?act=${
      adAccountID.split("act_")[1]
    }`;
  }

  if (platform === GOOGLE) {
    return `https://ads.google.com/aw/audiences/management`;
  }

  return "";
};

export const getBillingUrlMeta =
  (adAccountID = "") =>
  () => {
    return `https://business.facebook.com/billing_hub/payment_activity?asset_id=${
      adAccountID.split("act_")[1]
    }&business_id=&placement=ads_manager`;
  };

export const getBillingUrlTikTokBusiness =
  (adAccountID = "") =>
  () => {
    return `https://ads.tiktok.com/i18n/account/payment?aadvid=${adAccountID}`;
  };

export const getBillingUrlGoogleAds = () => () => {
  return `https://ads.google.com/aw/billing/summary`;
};

export const getCreateConversionUrlGoogleAds = () => () => {
  return "https://ads.google.com/aw/conversions/selection";
};
export const getIntegrationKey = (
  platform,
  differenceTikTokBusiness = false
) => {
  switch (platform) {
    case META:
      return FACEBOOK;

    case TIKTOK:
      return differenceTikTokBusiness ? TIKTOK_BUSINESS : TIKTOK;

    default:
      return platform;
  }
};
export const getPlatformFromIntegrationKey = (integrationKey) => {
  switch (integrationKey) {
    case FACEBOOK:
      return META;

    case TIKTOK:
    case TIKTOK_BUSINESS:
      return TIKTOK;

    default:
      return integrationKey;
  }
};

export const getRoleCapability = (capability) => (_, getState) => {
  const role = getState()?.auth?.user?.role;
  return hasRoleCapability(role, capability);
};

export const getRoleCapabilities = () => (_, getState) => {
  const role = getState()?.auth?.user?.role;
  return resolveRoleCapabilities(role);
};

export const getMaxCampaignsBySubscriptionPlan = () => (_, getState) => {
  const subscriptionPlan = getState()?.payments?.subscriptionPlan;
  const renewDate = getState()?.payments?.subscriptionPlanInfo?.renewDate;
  const maxCampaigns =
    getState()?.payments?.subscriptionPlanInfo?.benefits?.campaigns || 0;

  if (!subscriptionPlan && maxCampaigns > 0) return maxCampaigns;
  if (!subscriptionPlan || !renewDate) return 0;

  const recurrent =
    getState()?.payments?.subscriptionPlanInfo?.recurrent || "monthly";
  const recurrentMultiplier = RECURRENT_MULTIPLIER[recurrent] || 1;
  return maxCampaigns * recurrentMultiplier;
};

export const getIsBlockedActions =
  (props = {}) =>
  async (dispatch, getState) => {
    const {
      action,
      showUsageConfirmationAlert = true,
      showPaywall = true,
      multiplier = 1,
      campaignUsageContext,
    } = props;

    const typeOfPlans = getState()?.payments?.typeOfPlans;
    const creditPricingVersion = getState()?.payments?.creditPricingVersion;

    let effectiveMultiplier = multiplier;
    if (
      action?.type === ADS_ACTION_TYPES.GENERATE_CAMPAIGN &&
      typeOfPlans === PLAN_TYPE_CREDITS
    ) {
      effectiveMultiplier = resolveCampaignUsageMultiplier({
        creditPricingVersion,
        campaignExists: campaignUsageContext?.campaignExists ?? false,
        platform: campaignUsageContext?.platform,
        generatedPlatforms: campaignUsageContext?.generatedPlatforms,
      });
    }
    const isTrial = getState()?.payments?.subscriptionPlanInfo?.isTrial;
    const isPlanActive = getState()?.payments?.isPlanActive;

    const isPackagesPlan = typeOfPlans === PLAN_TYPE_PACKAGES;
    const isCreditsPlan = typeOfPlans === PLAN_TYPE_CREDITS;

    let isBlocked = false;
    let spend = null;
    let credits = null;
    let meterKey = null;
    let isBrandCapBlock = false;

    if (!isPlanActive && !isTrial) {
      if (showPaywall) {
        dispatch(setShowModalSelectSubscription(true));
      }
      return true;
    }

    // BLOCK ADD CONTENT GALLERY BY STORAGE (dynamic maxBytes limit)
    if (action?.type === ADS_ACTION_TYPES.ADD_CONTENT_GALLERY) {
      const storage = getState()?.user?.storage || {};
      const usedBytes = storage.usedBytes ?? 0;
      const maxBytes = storage.maxBytes || MAX_BYTES_STORAGE;
      isBlocked = usedBytes >= maxBytes;
      if (isBlocked && showPaywall) {
        dispatch(setModalOpenState("isPanelStorageFull", true));
      }
      return isBlocked;
    }

    // BLOCK VIDEOS AND ANIMATE IMAGE FOR TRIAL (packages plan uses videos for animateImage)
    if (
      isTrial &&
      (action.type === ADS_ACTION_TYPES.VIDEOS ||
        action.type === ADS_ACTION_TYPES.ANIMATE_IMAGE)
    ) {
      isBlocked = true;
      if (showPaywall) {
        dispatch(setShowModalSelectSubscription(true));
      }
      return isBlocked;
    }

    // GENERATE_TEXT: no credit charge or balance check; AppSumo-only BYOK/credits confirm
    if (action?.type === ADS_ACTION_TYPES.GENERATE_TEXT) {
      if (!showUsageConfirmationAlert) {
        return false;
      }

      const isAppSumo = (await dispatch(startGetIsAppSumo())) === true;
      if (!isAppSumo) {
        return false;
      }

      let isByokUsageConfirmBlocked = false;
      const byokConfiguration =
        (await dispatch(startGetByokConfiguration())) || {};

      const confirmUsageProps = {
        typeOfPlans,
        actionType: action.type,
        hideCreditsSummary: true,
        defaultPaymentMode: byokConfiguration?.defaultPaymentMode || "credits",
        onChangePaymentMode: (value) =>
          dispatch(
            startSaveByokConfiguration({
              defaultPaymentMode: value,
            })
          ),
        onConfirmBlockedChange: (blocked) => {
          isByokUsageConfirmBlocked = Boolean(blocked);
        },
      };

      const result = await ConfirmAlert({
        title: i18next.t(CONFIRMATION_USAGE_TEXT),
        html: <ConfirmUsage {...confirmUsageProps} />,
        confirmButtonText: i18next.t(BUTTON_CONTINUE),
        preConfirm: () => {
          if (isByokUsageConfirmBlocked) return false;
        },
      });

      if (!result.isConfirmed) {
        return true;
      }

      return false;
    }

    if (isPackagesPlan) {
      const adsCampaignsIDsByAgency =
        getState()?.adsCampaigns?.adsCampaignsIDsByAgency;
      const maxCampaigns = dispatch(getMaxCampaignsBySubscriptionPlan());
      const currentUsage =
        getState()?.payments?.subscriptionPlanInfo?.usage || {};
      const isTrial = getState()?.payments?.subscriptionPlanInfo?.isTrial;

      // To verify campaign generation, it is done by calculating the campaigns across the entire agency
      if (action.type === ADS_ACTION_TYPES.GENERATE_CAMPAIGN) {
        if (!maxCampaigns || adsCampaignsIDsByAgency.length >= maxCampaigns) {
          isBlocked = true;
        }

        credits = maxCampaigns - adsCampaignsIDsByAgency.length;
        // Only block by usage if action is provided
      } else if (action) {
        // For ADS_ACTION_TYPE_MESSAGES, only apply if isTrial, otherwise never block
        if (action.type === ADS_ACTION_TYPES.MESSAGES && !isTrial) {
          return false;
        }

        if (ACTIONS_FREE_BY_TYPE_OF_PLAN[typeOfPlans].includes(action.type)) {
          return false;
        }

        const resolvedActionCost = resolveActionCostForPlanType(
          action,
          typeOfPlans
        );
        const currentUsageAction =
          currentUsage[resolvedActionCost.usageKey] || 0;
        spend = resolvedActionCost.amount * effectiveMultiplier;
        isBlocked = currentUsageAction < spend;
        meterKey = resolvedActionCost.meterKey;
        credits = currentUsageAction;
      }
    }

    if (isCreditsPlan) {
      if (ACTIONS_FREE_BY_TYPE_OF_PLAN[typeOfPlans].includes(action.type)) {
        return false;
      }
      const currentUsage =
        getState()?.payments?.subscriptionPlanInfo?.usage || {};
      const resolvedActionCost = resolveActionCostForPlanType(
        action,
        typeOfPlans
      );
      const currentBalance = currentUsage[resolvedActionCost.usageKey] || 0;

      meterKey = resolvedActionCost.meterKey;
      spend = resolvedActionCost.amount * effectiveMultiplier;
      isBlocked = currentBalance < spend;

      const usageGate = resolveBrandUsageGateFromState({
        getState,
        usageKey: resolvedActionCost.usageKey,
        agencyRemaining: currentBalance,
        requiredSpend: spend,
      });
      credits = usageGate.displayBalance;

      if (!isBlocked && usageGate.isCapBlock) {
        isBlocked = true;
        isBrandCapBlock = true;
      }
    }

    const shouldOfferPaymentModeSelection =
      showUsageConfirmationAlert &&
      isCreditsPlan &&
      spend > 0 &&
      (meterKey === PLAN_TYPE_CREDITS || meterKey === ADS_ACTION_TYPES.VIDEOS);

    let isAppSumo = false;
    const needsAppSumoCheck =
      shouldOfferPaymentModeSelection ||
      (showPaywall && isBlocked && isCreditsPlan);

    if (needsAppSumoCheck) {
      isAppSumo = (await dispatch(startGetIsAppSumo())) === true;
    }

    const skipPaywallForAppSumoPaymentMode =
      isAppSumo && shouldOfferPaymentModeSelection;

    const openBlockedActionPaywall = async () => {
      if (!showPaywall) return;

      const isVideoAction = action?.type === ADS_ACTION_TYPES.VIDEOS;
      const isAnimateImageOnPackages =
        action?.type === ADS_ACTION_TYPES.ANIMATE_IMAGE && isPackagesPlan;
      if (isVideoAction || isAnimateImageOnPackages) {
        if (isAppSumo) {
          dispatch(setModalOpenState("isModalPurchaseCredits", true));
          return;
        }
        if (
          usesVideoMeterOnCreditsPlan(
            getState()?.payments?.creditPricingVersion
          )
        ) {
          const result = await ConfirmAlert({
            title: i18next.t(VIDEO_INSUFFICIENT_CREDITS_ALERT_TITLE),
            text: i18next.t(VIDEO_INSUFFICIENT_CREDITS_ALERT_INTRO),
            showThirdButton: true,
            showCancelButton: false,
            confirmButtonText: i18next.t(BUY_CREDITS),
            thirdButtonText: i18next.t(MODAL_PURCHASE_CREDITS_UPGRADE_PLAN),
            icon: "warning",
          });
          if (result.isConfirmed) {
            dispatch(setModalOpenState("isModalPurchaseCredits", true));
          } else if (result.isDenied) {
            dispatch(setShowModalSelectSubscription(true));
          }
          return;
        }
      }
      dispatch(setShowModalSelectSubscription(true));
    };

    if (showPaywall && isBlocked && !skipPaywallForAppSumoPaymentMode) {
      if (isBrandCapBlock) {
        await dispatch(showBrandUsageLimitAlert());
      } else {
        await openBlockedActionPaywall();
      }
    }

    if (shouldOfferPaymentModeSelection && (!isBlocked || isAppSumo)) {
      let isByokUsageConfirmBlocked = false;
      let selectedPaymentMode = "credits";

      const confirmUsageProps = {
        typeOfPlans,
        actionType: action?.type,
        meterKey,
        spend,
        credits,
        onConfirmBlockedChange: (blocked) => {
          isByokUsageConfirmBlocked = Boolean(blocked);
        },
      };

      if (isAppSumo) {
        const byokConfiguration =
          (await dispatch(startGetByokConfiguration())) || {};
        selectedPaymentMode =
          byokConfiguration?.defaultPaymentMode || "credits";
        confirmUsageProps.defaultPaymentMode = selectedPaymentMode;
        confirmUsageProps.onChangePaymentMode = (value) => {
          selectedPaymentMode = value;
          dispatch(
            startSaveByokConfiguration({
              defaultPaymentMode: value,
            })
          );
        };
      }

      const result = await ConfirmAlert({
        title: i18next.t(CONFIRMATION_USAGE_TEXT),
        html: <ConfirmUsage {...confirmUsageProps} />,
        confirmButtonText: i18next.t(BUTTON_CONTINUE),
        preConfirm: () => {
          if (isByokUsageConfirmBlocked) return false;
        },
      });
      if (!result.isConfirmed) return true;
      if (isAppSumo && selectedPaymentMode === "credits" && credits < spend) {
        await openBlockedActionPaywall();
        return true;
      }
      return false;
    }

    return isBlocked;
  };

export const getIsFreeTrialFinished = () => (_, getState) => {
  const subscriptionPlan = getState().payments.subscriptionPlan;
  const subscriptionPlanInfo = getState().payments.subscriptionPlanInfo;
  const finishTrialDate = subscriptionPlanInfo?.finishTrialDate;
  const now = new Date("01/22/2025");
  const isTrialExpired = finishTrialDate
    ? new Date(finishTrialDate).getTime() < now.getTime()
    : false;

  return isTrialExpired && !subscriptionPlan;
};

export const getCurrencyAdAccountPlatform =
  ({ platform, integrationID, adAccountID }) =>
  (_, getState) => {
    const integrations = getState()?.user?.integrations;
    const integrationKey = getIntegrationKey(platform, true);
    const integrationsPlatform = integrations[integrationKey];
    const integrationPlatform = integrationsPlatform?.[integrationID];

    if (platform === META) {
      const advertiser = integrationPlatform?.businessAccounts?.[adAccountID];
      return advertiser?.currency || "";
    }
    if (platform === GOOGLE) {
      const advertiser = integrationPlatform?.advertisers?.[adAccountID];
      return advertiser?.currency || "";
    }
    if (platform === TIKTOK) {
      return integrationPlatform?.currency || "";
    }
    if (platform === LINKEDIN) {
      return integrationPlatform?.advertisers?.[adAccountID]?.currency || "";
    }
  };
